// ─────────────────────────────────────────────────────────────────────────────
// AboutPage.jsx
// All content from companyInfo.js — edit there, updates here automatically
// ─────────────────────────────────────────────────────────────────────────────

import { useRef, useState, useEffect }  from 'react'
import PageHero                          from '@components/common/PageHero'
import Button                            from '@components/common/Button'
import CtaBanner                         from '@components/common/CtaBanner'
import { companyInfo }                   from '@data/companyInfo'
import SEOHead                           from '@components/common/SEOHead'

// ── Images — uncomment when ready ────────────────────────────────────────────
import aboutHero    from '@assets/images/about/about-home-02.jpg'
import customerImg  from '@assets/images/about/customer-care.jpg'
import involvedImg  from '@assets/images/about/global-business.jpg'

// ── Value card images — add your images here when ready ──────────────────────
import qualityImg        from '@assets/images/about/values/quality.png'
import expertiseImg      from '@assets/images/about/values/expertise.png'
import partnershipImg    from '@assets/images/about/values/Partnership.png'
import performanceImg    from '@assets/images/about/values/performance.png'
import sustainabilityImg from '@assets/images/about/values/sustainability.png'
import innovationImg     from '@assets/images/about/values/innovation.png'

// Set to null until images are ready — placeholder will show automatically
const VALUE_IMAGES = {
  quality:        qualityImg, // qualityImg
  expertise:      expertiseImg, // expertiseImg
  partnership:    partnershipImg, // partnershipImg
  performance:    performanceImg, // performanceImg
  sustainability: sustainabilityImg, // sustainabilityImg
  innovation:     innovationImg, // innovationImg
}

// ── useFadeIn hook ────────────────────────────────────────────────────────────
function useFadeIn(threshold = 0.12) {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true) },
      { threshold }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, vis]
}

// ── Green circle check icon (MIAVIT style) ────────────────────────────────────
const CheckIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill="#007935"/>
    <path d="M5.5 10L8.5 13L14.5 7"
      stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// ── White circle check icon (on green bg) ─────────────────────────────────────
const CheckIconWhite = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill="rgba(255,255,255,0.25)"/>
    <path d="M5.5 10L8.5 13L14.5 7"
      stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// ── Image placeholder ─────────────────────────────────────────────────────────
const ImgPlaceholder = ({ label }) => (
  <div style={{
    width: '100%', height: '100%', minHeight: '280px',
    background: 'linear-gradient(135deg, #e8e4e0 0%, #d4cec9 100%)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '10px',
  }}>
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
      stroke="#b0aaa4" strokeWidth="1" strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
    <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'11px', color:'#b0aaa4', margin:0, textAlign:'center' }}>
      {label}
    </p>
  </div>
)

// ── Value card image placeholder (smaller, for the card visual zone) ──────────
const ValueImgPlaceholder = ({ icon, color, bg }) => (
  <div style={{
    width: '100%', height: '100%',
    background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      width: '72px', height: '72px',
      background: 'rgba(255,255,255,0.18)',
      borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {icon}
      </svg>
    </div>
  </div>
)

// ── Data ──────────────────────────────────────────────────────────────────────
const WHY_SALIENT = [
  'Science-based solutions',
  'Trusted global inputs',
  'Expert technical support',
  'Poultry, dairy & swine focus',
  'Compliant and sustainable',
  'Long-term partnerships',
  'Diverse product range',
]

const OUR_PRODUCTS_LIST = [
  'Vitamin & Mineral Supplements',
  'Gut Health',
  'Respiratory Health',
  'Antibiotic Alternatives',
  'Feed Additives',
  'Vitamin & Mineral Premix',
  'Electrolytes & Energy',
  'Liver Health',
  'Amino acid supplements',
  'Phytogenic feed and water supplement solutions'
]

// ── Value cards config ────────────────────────────────────────────────────────
// Each card has:
//   icon       — SVG path content
//   iconColor  — icon stroke color (used in placeholder)
//   gradientBg — gradient shown in placeholder until real image is added
//   imageKey   — key into VALUE_IMAGES to swap in a real photo
//   imageAlt   — alt text for the real image
//
// When you add real images, set VALUE_IMAGES[key] = yourImport above.
// The card will automatically switch from placeholder → real photo.

const VALUE_CARDS = [
  {
    key:        'quality',
    icon: (
      <>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          strokeLinecap="round" strokeLinejoin="round"/>
      </>
    ),
    iconColor:  '#f59e0b',
    gradientBg: 'linear-gradient(135deg, #78350f 0%, #d97706 100%)',
    imageAlt:   'Quality — premium certified inputs',
  },
  {
    key:        'expertise',
    icon: (
      <>
        <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/>
        <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
      </>
    ),
    iconColor:  '#60a5fa',
    gradientBg: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    imageAlt:   'Expertise — science-driven advisory',
  },
  {
    key:        'partnership',
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </>
    ),
    iconColor:  '#a78bfa',
    gradientBg: 'linear-gradient(135deg, #3b0764 0%, #7c3aed 100%)',
    imageAlt:   'Partnership — long-term collaboration',
  },
  {
    key:        'performance',
    icon: (
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    ),
    iconColor:  '#34d399',
    gradientBg: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
    imageAlt:   'Performance — measurable results',
  },
  {
    key:        'sustainability',
    icon: (
      <>
        <path d="M12 22V12M12 12C12 6 20 6 20 6s0 6-8 6zM12 12C12 6 4 6 4 6s0 6 8 6z"/>
        <path d="M8 22h8"/>
      </>
    ),
    iconColor:  '#86efac',
    gradientBg: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',
    imageAlt:   'Sustainability — resilient farming',
  },
  {
    key:        'innovation',
    icon: (
      <>
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4l3 3"/>
      </>
    ),
    iconColor:  '#fb923c',
    gradientBg: 'linear-gradient(135deg, #431407 0%, #ea580c 100%)',
    imageAlt:   'Innovation — global nutrition science',
  },
]

// ── ValueCard component ───────────────────────────────────────────────────────
function ValueCard({ val, cardCfg, visible, delay }) {
  const img = VALUE_IMAGES[cardCfg.key]

  return (
    <div
      style={{
        background:  '#ffffff',
        overflow:    'hidden',
        display:     'flex',
        flexDirection: 'column',
        opacity:     visible ? 1 : 0,
        transform:   visible ? 'translateY(0)' : 'translateY(24px)',
        transition:  `all 0.55s ease ${delay}s`,
      }}
    >
      {/* ── Visual zone: real image OR gradient placeholder ── */}
      <div style={{ width: '100%', height: '160px', overflow: 'hidden', flexShrink: 0, background: '#e8f2ec', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {img ? (
          <img
            src={img}
            alt={cardCfg.imageAlt}
            style={{ width: 'auto', height: '60%', maxWidth: '80%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <ValueImgPlaceholder
            icon={cardCfg.icon}
            color={cardCfg.iconColor}
            bg={cardCfg.gradientBg}
          />
        )}
      </div>

      {/* ── Text zone ── */}
      <div style={{ padding: '24px 24px 28px' }}>
        {/* Title with amber underline accent */}
        <h3 style={{
          fontFamily:    "'DM Sans', sans-serif",
          fontSize:      '13px',
          fontWeight:    700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color:         '#1a1a16',
          margin:        '0 0 8px',
          paddingBottom: '8px',
          borderBottom:  '2px solid #ed9716',
          display:       'inline-block',
        }}>
          {val.title}
        </h3>

        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   '13.5px',
          color:      '#55544e',
          lineHeight: 1.65,
          margin:     '12px 0 0',
        }}>
          {val.description}
        </p>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AboutPage() {
  const [sec1Ref, sec1Vis] = useFadeIn()
  const [sec2Ref, sec2Vis] = useFadeIn()
  const [sec3Ref, sec3Vis] = useFadeIn()
  const [sec4Ref, sec4Vis] = useFadeIn()
  const [sec5Ref, sec5Vis] = useFadeIn()

  return (
    <main>
      <SEOHead
        title="About Us"
        description="Learn about Salient Agrinutrition — our mission, values, and commitment to elevating animal nutrition standards across Sri Lanka's poultry, dairy, and livestock sectors."
        path="/about"
      />

      {/* ══════════════════════════════════════════
          01 — PageHero
      ══════════════════════════════════════════ */}
      <PageHero
        title="About Us"
        subtitle="A globally connected livestock nutrition partner committed to advancing Sri Lanka's animal production sector."
        breadcrumbs={[
          { label: 'Home',     to: '/' },
          { label: 'About Us' },
        ]}
      />

      {/* ══════════════════════════════════════════
          02 — Full-width team photo
      ══════════════════════════════════════════ */}
      <div style={{ width: '100%', height: 'clamp(220px, 38vw, 480px)', overflow: 'hidden', position: 'relative' }}>
        {aboutHero ? (
          <img src={aboutHero} alt="Salient Agrinutrition team"
            style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center' }} />
        ) : (
          <ImgPlaceholder label="src/assets/images/about/about-home.jpg" />
        )}

        {/* Tagline overlay */}
        <div style={{
          position:       'absolute',
          inset:          '0',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
        }}>
          <div style={{
            display:    'flex',
            alignItems: 'stretch',
            gap:        '0',
          }}>
            {/* Green bar */}
            <div style={{
              width:      '5px',
              background: '#007935',
              flexShrink: 0,
            }} />
            {/* Text block */}
            <div style={{
              background:     'rgba(10, 20, 12, 0.62)',
              padding:        'clamp(16px, 2.5vw, 28px) clamp(24px, 4vw, 44px)',
              backdropFilter: 'blur(6px)',
            }}>
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      'clamp(1.5rem, 3.5vw, 2.75rem)',
                fontWeight:    300,
                letterSpacing: '0.03em',
                color:         '#ffffff',
                margin:        '0',
                lineHeight:    1.15,
              }}>
                Global quality, Local Care
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          03 — Company intro: text LEFT + green checklist RIGHT
      ══════════════════════════════════════════ */}
      <section style={{ background: '#ffffff', padding: '72px 0' }}>
        <div style={{ maxWidth:'var(--container-max)', margin:'0 auto', padding:'0 var(--container-padding)' }}>
          <div
            ref={sec1Ref}
            className="about-grid-2col"
            style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'48px', alignItems:'start' }}
          >

            {/* LEFT: Company text */}
            <div style={{
              opacity:    sec1Vis ? 1 : 0,
              transform:  sec1Vis ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease',
            }}>
              <h2 style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      'clamp(2rem, 4vw, 3.25rem)',
                fontWeight:    300,
                letterSpacing: '0.02em',
                color:         '#007935',
                margin:        '0 0 8px', lineHeight: 1.1,
              }}>
                Company
              </h2>
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      '15px', fontWeight: 400,
                color:         '#88877f', fontStyle: 'italic',
                margin:        '0 0 24px',
              }}>
                The trusted partner for livestock nutrition.
              </p>

              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8,
                margin: '0 0 16px',
              }}>
                Salient Agrinutrition, founded by Mr. Kumara Nissanka, with its main warehouse located in Kuliyapitiya, was established in 2017 and is now managed by the second generation.
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8,
                margin: '0 0 16px',
              }}>
                Since the beginning, this family-owned company has been guided by one clear principle: &ldquo;Prevention first, medication only when necessary.&rdquo;
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8,
                margin: '0 0 16px',
              }}>
                Salient Agrinutrition is a globally connected livestock nutrition partner committed to advancing Sri Lanka&rsquo;s animal production sector through science-driven and performance-focused solutions.
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8,
                margin: 0,
              }}>
                We source and distribute premium feed additives, mineral solutions, performance enhancers, gut health products, and specialty nutritional inputs from internationally recognized manufacturers. By connecting global innovation with local application, we bring advanced nutritional technologies to poultry, dairy, and swine production systems across Sri Lanka.
              </p>
            </div>

            {/* RIGHT: Green "Why Salient?" box */}
            <div style={{
              opacity:    sec1Vis ? 1 : 0,
              transform:  sec1Vis ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease 0.15s',
              background: '#007935',
              padding:    '36px 32px',
            }}>
              <h3 style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      'clamp(1.25rem, 2vw, 1.625rem)',
                fontWeight:    400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color:         '#ffffff', margin: '0 0 24px',
              }}>
                Why Salient Agrinutrition?
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {WHY_SALIENT.map((pt, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    opacity:    sec1Vis ? 1 : 0,
                    transform:  sec1Vis ? 'translateX(0)' : 'translateX(-12px)',
                    transition: `all 0.4s ease ${0.2 + i * 0.07}s`,
                  }}>
                    <CheckIconWhite size={18} />
                    <span style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px', color: 'rgba(255,255,255,0.90)', lineHeight: 1.55,
                    }}>
                      {pt}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          04 — Our Solutions: text + checklist 2-col
      ══════════════════════════════════════════ */}
      <section style={{ background: '#f5f5f3', padding: '72px 0' }}>
        <div style={{ maxWidth:'var(--container-max)', margin:'0 auto', padding:'0 var(--container-padding)' }}>
          <div
            ref={sec2Ref}
            className="about-grid-2col"
            style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'64px', alignItems:'start' }}
          >

            {/* LEFT: text */}
            <div style={{
              opacity:    sec2Vis ? 1 : 0,
              transform:  sec2Vis ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease',
            }}>
              <h2 style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
                fontWeight:    400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color:         '#007935', margin: '0 0 6px',
              }}>
                Our Solutions
              </h2>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13.5px', color: '#88877f', fontStyle: 'italic',
                margin: '0 0 20px',
              }}>
                Best quality and highest standard
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: '0 0 16px',
              }}>
                We offer you a wide range of Science-driven nutritional solutions for poultry, dairy and swine production systems.
                By connecting global innovation with local
                application, we integrate advanced nutritional technologies across Sri Lanka's
                animal production sector.
              </p>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: '0 0 28px',
              }}>
                All our inputs are sourced from internationally recognized manufacturers
                with proven track records. We only work with reliable and certified suppliers
                to ensure product safety and regulatory compliance.
              </p>
              <Button to="/products" variant="green">
                View All Products
              </Button>
            </div>

            {/* RIGHT: checklist */}
            <div style={{
              opacity:    sec2Vis ? 1 : 0,
              transform:  sec2Vis ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease 0.15s',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {OUR_PRODUCTS_LIST.map((pt, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    opacity:    sec2Vis ? 1 : 0,
                    transform:  sec2Vis ? 'translateX(0)' : 'translateX(12px)',
                    transition: `all 0.4s ease ${0.15 + i * 0.07}s`,
                  }}>
                    <CheckIcon size={18} />
                    <span style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px', color: '#44443e', lineHeight: 1.55,
                    }}>
                      {pt}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          05 — "The customer in focus"
          Image LEFT + text + button RIGHT
      ══════════════════════════════════════════ */}
      <section style={{ background: '#ffffff', padding: '0' }}>
        <div
          ref={sec3Ref}
          className="about-alt-row"
          style={{ display:'grid', gridTemplateColumns:'1fr 1fr', minHeight:'clamp(280px, 36vw, 440px)' }}
        >
          {/* Image LEFT */}
          <div style={{
            overflow: 'hidden',
            opacity:    sec3Vis ? 1 : 0,
            transition: 'opacity 0.7s ease',
          }}>
            {customerImg ? (
              <img src={customerImg} alt="Customer in focus"
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            ) : (
              <ImgPlaceholder label="src/assets/images/about/customer-focus.jpg" />
            )}
          </div>

          {/* Text RIGHT */}
          <div style={{
            padding:        'clamp(40px, 5vw, 72px) clamp(32px, 5vw, 64px)',
            display:        'flex', flexDirection: 'column', justifyContent: 'center',
            opacity:        sec3Vis ? 1 : 0,
            transform:      sec3Vis ? 'translateX(0)' : 'translateX(24px)',
            transition:     'all 0.6s ease 0.15s',
          }}>
            <h2 style={{
              fontFamily:    "'DM Sans', sans-serif",
              fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
              fontWeight:    400, letterSpacing: '0.04em', textTransform: 'uppercase',
              color:         '#007935', margin: '0 0 18px',
            }}>
              The customer in focus
            </h2>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: '0 0 28px',
            }}>
              Our team includes trained, technical experts who constantly offer individual,
              customer-oriented solution concepts to ensure high service quality. The farmer
              and producer is always the focus of our attention.
            </p>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: '0 0 32px',
            }}>
              Through our partnerships, we create trust that we want to continue
              strengthening every day - providing technical alignment, training, and
              responsive service across Sri Lanka.
            </p>
            <div>
              <Button variant="green" to="/contact">
                Contact us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          06 — "Globally connected"
          Text LEFT + image RIGHT
      ══════════════════════════════════════════ */}
      <section style={{ background: '#f5f5f3', padding: '0' }}>
        <div
          ref={sec4Ref}
          className="about-alt-row"
          style={{ display:'grid', gridTemplateColumns:'1fr 1fr', minHeight:'clamp(280px, 36vw, 440px)' }}
        >
          {/* Text LEFT */}
          <div style={{
            padding:    'clamp(40px, 5vw, 72px) clamp(32px, 5vw, 64px)',
            display:    'flex', flexDirection: 'column', justifyContent: 'center',
            opacity:    sec4Vis ? 1 : 0,
            transform:  sec4Vis ? 'translateX(0)' : 'translateX(-24px)',
            transition: 'all 0.6s ease',
          }}>
            <h2 style={{
              fontFamily:    "'DM Sans', sans-serif",
              fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
              fontWeight:    400, letterSpacing: '0.04em', textTransform: 'uppercase',
              color:         '#007935', margin: '0 0 18px',
            }}>
              Globally connected
            </h2>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: '0 0 16px',
            }}>
              For the benefit of animal health, Salient Agrinutrition connects Sri Lanka's
              livestock industry to globally recognized manufacturers across Germany,
              India, and Vietnam.
            </p>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14.5px', color: '#55544e', lineHeight: 1.8, margin: 0,
            }}>
              Our supplier network spans internationally certified manufacturers whose
              products are proven in markets across more than 80 countries - delivering
              that same world-class quality directly to Sri Lanka's farmers and producers.
            </p>
          </div>

          {/* Image RIGHT */}
          <div style={{
            overflow: 'hidden',
            opacity:    sec4Vis ? 1 : 0,
            transition: 'opacity 0.7s ease 0.15s',
          }}>
            {involvedImg ? (
              <img src={involvedImg} alt="Globally connected"
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            ) : (
              <ImgPlaceholder label="src/assets/images/about/involved.jpg" />
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          07 — Our Values
          ── UPDATED: image-card format ──
          3-col cards with image zone (gradient placeholder → real photo)
          White bg section to contrast from the original green block
      ══════════════════════════════════════════ */}
      <section style={{ background: '#007935', padding: '72px 0' }}>
        <div style={{ maxWidth:'var(--container-max)', margin:'0 auto', padding:'0 var(--container-padding)' }}>

          {/* Section header */}
          <h2 style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
            fontWeight:    400, letterSpacing: '0.04em', textTransform: 'uppercase',
            color:         '#ffffff', margin: '0 0 8px',
          }}>
            Our Values
          </h2>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px', color: 'rgba(255,255,255,0.65)',
            margin: '0 0 40px',
          }}>
            The principles that guide everything we do at Salient Agrinutrition.
          </p>

          {/* Cards grid */}
          <div
            ref={sec5Ref}
            className="values-grid"
            style={{
              display:             'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap:                 '16px',
            }}
          >
            {companyInfo.values.map((val, i) => {
              const cardCfg = VALUE_CARDS.find(c => c.key === val.icon) || VALUE_CARDS[i % VALUE_CARDS.length]
              return (
                <ValueCard
                  key={val.title}
                  val={val}
                  cardCfg={cardCfg}
                  visible={sec5Vis}
                  delay={i * 0.08}
                />
              )
            })}
          </div>

          {/* Helper note (remove in production) */}
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '11px', color: 'rgba(255,255,255,0.35)',
            margin: '24px 0 0', fontStyle: 'italic',
          }}>
            {/* To add real images: set VALUE_IMAGES keys in AboutPage.jsx → import your photos → the gradient placeholders will auto-replace */}
          </p>

        </div>
      </section>



      <style>{`
        @media (max-width: 900px) {
          .about-grid-2col { grid-template-columns: 1fr !important; gap: 36px !important; }
          .about-alt-row   { grid-template-columns: 1fr !important; }
          .values-grid     { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .values-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </main>
  )
}